/**
 * AssetAllocationCard.jsx — Donut chart showing asset-class allocation
 * (Equity, Gold/Commodities, MF/Index Funds, future buckets).
 *
 * Uses same Recharts pattern as SectorAllocationCard.
 */

import React, { useState, useCallback } from 'react';
import {
    PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Sector,
} from 'recharts';

/* ── helpers ── */
const fmt = (n, d = 2) => {
    if (n == null || isNaN(n)) return 'N/A';
    return Number(n).toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d });
};
const fmtCurrency = (n) => (n == null || isNaN(n) ? 'N/A' : `₹${fmt(n)}`);
const fmtPct = (n) => (n == null || isNaN(n) ? 'N/A' : `${fmt(n)}%`);

const RADIAN = Math.PI / 180;

const ASSET_COLORS = {
    'Equity': '#7c3aed',
    'Gold / Commodities': '#eab308',
    'MF / Index Funds': '#3b82f6',
    'Fixed Income': '#22c55e',
    'Fixed Deposits': '#06b6d4',
    'Cash': '#94a3b8',
};

const getColor = (name, idx) => ASSET_COLORS[name] || ['#f97316', '#ef4444', '#8b5cf6', '#ec4899'][idx % 4];

/* ── Outer label ── */
const renderLabel = ({ cx, cy, midAngle, outerRadius, name, percent }) => {
    if (percent < 0.02) return null;
    const radius = outerRadius + 28;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
        <text x={x} y={y} fill="var(--text-2)" textAnchor={x > cx ? 'start' : 'end'}
            dominantBaseline="central" fontSize={12} fontWeight={500}>
            {name} ({(percent * 100).toFixed(1)}%)
        </text>
    );
};

/* ── Active shape on hover ── */
const renderActiveShape = (props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent } = props;
    return (
        <g>
            <text x={cx} y={cy - 8} textAnchor="middle" fill="var(--text-1)" fontSize={15} fontWeight={600}>
                {payload.name}
            </text>
            <text x={cx} y={cy + 14} textAnchor="middle" fill="var(--text-2)" fontSize={12}>
                {(percent * 100).toFixed(1)}%
            </text>
            <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius + 8}
                startAngle={startAngle} endAngle={endAngle} fill={fill} />
            <Sector cx={cx} cy={cy} innerRadius={outerRadius + 12} outerRadius={outerRadius + 16}
                startAngle={startAngle} endAngle={endAngle} fill={fill} />
        </g>
    );
};

/* ── Custom tooltip ── */
const ChartTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
        <div className="sector-tooltip">
            <div className="sector-tooltip-header">
                <span className="sector-tooltip-dot" style={{ background: d.fill }} />
                <strong>{d.name}</strong>
            </div>
            <div className="sector-tooltip-row">
                <span>Value</span><span>{fmtCurrency(d.value)}</span>
            </div>
            <div className="sector-tooltip-row">
                <span>Weight</span><span>{fmtPct(d.pct)}</span>
            </div>
            {d.status === 'not_connected' && (
                <div className="sector-tooltip-row" style={{ color: 'var(--muted)', fontStyle: 'italic' }}>
                    <span>Not connected</span>
                </div>
            )}
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */
const AssetAllocationCard = ({ data }) => {
    const [activeIndex, setActiveIndex] = useState(-1);
    const onEnter = useCallback((_, i) => setActiveIndex(i), []);
    const onLeave = useCallback(() => setActiveIndex(-1), []);

    const buckets = data?.buckets ?? [];
    const totalValue = data?.totalMarketValue ?? buckets.reduce((s, b) => s + (b.marketValue || b.value || 0), 0);

    if (!buckets.length) {
        return (
            <section className="portfolio-section">
                <h3 className="portfolio-section-title">Asset-Class Allocation</h3>
                <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
                    No asset allocation data available.
                </p>
            </section>
        );
    }

    const chartData = buckets.map((b, i) => ({
        name: b.label,
        value: b.marketValue || b.value || 0,
        pct: b.weightPct ?? b.pct,
        status: b.status,
        fill: getColor(b.label, i),
    }));

    return (
        <section className="portfolio-section asset-allocation-card">
            <h3 className="portfolio-section-title">Asset-Class Allocation</h3>
            <p className="sector-subtitle">How your portfolio is split across asset classes</p>

            <div className="sector-chart-container">
                <div className="sector-pie-wrap">
                    <ResponsiveContainer width="100%" height={480}>
                        <PieChart>
                            <Pie
                                data={chartData}
                                cx="50%"
                                cy="50%"
                                innerRadius={65}
                                outerRadius={120}
                                dataKey="value"
                                nameKey="name"
                                minAngle={3}
                                label={renderLabel}
                                labelLine={{ stroke: 'var(--text-3)', strokeWidth: 1 }}
                                activeIndex={activeIndex}
                                activeShape={renderActiveShape}
                                onMouseEnter={onEnter}
                                onMouseLeave={onLeave}
                                animationDuration={600}
                            >
                                {chartData.map((entry) => (
                                    <Cell key={entry.name} fill={entry.fill}
                                        stroke="var(--bg-card)" strokeWidth={2}
                                        opacity={entry.status === 'not_connected' ? 0.35 : 1} />
                                ))}
                            </Pie>
                            <Tooltip content={<ChartTooltip />} />
                        </PieChart>
                    </ResponsiveContainer>
                    <div className="sector-center-label">
                        <div className="sector-center-value">{fmtCurrency(totalValue)}</div>
                        <div className="sector-center-sub">Total Portfolio</div>
                    </div>
                </div>

                {/* Legend */}
                <div className="sector-legend-wrap">
                    <ul className="sector-legend">
                        {chartData.map((entry) => (
                            <li key={entry.name} className="sector-legend-item"
                                style={{ opacity: entry.status === 'not_connected' ? 0.5 : 1 }}>
                                <span className="sector-legend-dot" style={{ background: entry.fill }} />
                                <span className="sector-legend-label">{entry.name}</span>
                                <span className="sector-legend-pct">{fmtPct(entry.pct)}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    );
};

export default AssetAllocationCard;
